/**
 * Combined credential import status + provider refresh availability card.
 * Import start is owned by the parent page (CreditReport) via onRequestRefresh.
 */

import React, { useCallback, useMemo } from 'react';
import { Key, Upload, RefreshCw, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { useI18n } from '../../context/I18nContext';
import { useProviderRefreshAction } from '../../hooks/useProviderRefreshAction';
import {
  resolveProviderRefreshDate,
  REFRESH_STATUS_UNAVAILABLE_MESSAGE,
} from '../../utils/providerRefreshAction';
import { getProviderUrl } from '../../config/providerRegistry';
import { getSafeCreditReportTheme } from '../../utils/safeCreditReportTheme';
import ReportDateLines from './ReportDateLines';

const CR = 'creditReport';

const CredentialImportStatusCard = ({
  report,
  theme,
  sourceDisplay,
  canCredentialReimport = false,
  activeImportId = null,
  onUpload,
  onRequestRefresh,
  onNeedCredentials,
  onRefreshMetadataUpdated,
}) => {
  const { t } = useI18n();
  const safeTheme = useMemo(() => getSafeCreditReportTheme(theme), [theme]);
  const provider = report?.import_provider;

  const refreshEnabled = Boolean(canCredentialReimport && provider);

  const {
    data: refreshData,
    loading,
    hasCredentials,
    statusUnavailable,
    actionState,
    refetch,
  } = useProviderRefreshAction(provider, {
    enabled: refreshEnabled,
    hasSavedReport: true,
  });

  const canRefreshNow = Boolean(
    actionState?.can_refresh_now
    || actionState?.action === 'refresh_now'
    || refreshData?.can_refresh_now
    || refreshData?.banner?.can_reimport_now
  );

  const providerDisplayName = sourceDisplay?.origin?.provider
    || refreshData?.provider_display_name
    || provider;

  const refreshDateLabel = resolveProviderRefreshDate(refreshData);

  const importInFlight = Boolean(activeImportId);

  const handleManualRefresh = useCallback(() => {
    if (importInFlight || !provider) return;
    if (hasCredentials) {
      onRequestRefresh?.();
    } else {
      onNeedCredentials?.(provider);
    }
  }, [importInFlight, hasCredentials, onRequestRefresh, onNeedCredentials, provider]);

  const handlePurchase = useCallback(() => {
    if (importInFlight || !provider) return;
    const url = getProviderUrl(provider);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [importInFlight, provider]);

  if (!report?.import_provider && !report?.import_source) {
    return null;
  }

  const SourceIcon = sourceDisplay?.icon || Key;
  const showRefreshStatus = canCredentialReimport && !loading;
  const showPurchase = Boolean(
    actionState?.secondary_action === 'purchase'
    && !hasCredentials
    && !canCredentialReimport
  );

  const primaryRefreshLabel = refreshDateLabel
    ? t(`${CR}.refreshAvailableOn`, { date: refreshDateLabel })
    : t(`${CR}.refreshReport`);
  const accentGreen = safeTheme.green[700];
  const accentGreenSolid = safeTheme.green[600];

  return (
    <section
      className="relative overflow-hidden flex flex-col gap-3 p-3 sm:p-4"
      style={{
        background: safeTheme.card.bg,
        border: `1px solid ${canRefreshNow ? `${accentGreen}30` : safeTheme.card.border}`,
        borderRadius: '18px',
        boxShadow: safeTheme.card.shadow,
        backdropFilter: 'blur(16px) saturate(140%)',
      }}
      data-testid="credential-import-status-card"
    >
      <div style={{ position: 'absolute', inset: 0, background: safeTheme.card.innerGlow, pointerEvents: 'none' }} />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 relative z-10">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: safeTheme.shield.bg }}
          >
            <SourceIcon size={16} style={{ color: safeTheme.shield.color }} />
          </div>
          <div className="min-w-0">
            <h3 className="text-xs sm:text-sm font-extrabold mb-0.5" style={{ color: safeTheme.text.primary }}>
              {sourceDisplay?.label || t(`${CR}.sourceCredentialImport`)}
            </h3>
            <p className="text-[10px] sm:text-xs truncate" style={{ color: safeTheme.text.muted }}>
              {t(`${CR}.reportIdOnlyLine`, {
                id: report?.report_id?.slice(0, 15) || t(`${CR}.notAvailable`),
              })}
            </p>
            <ReportDateLines
              report={report}
              t={t}
              theme={safeTheme}
              align="left"
              className="mt-1"
              testId="credential-card-dates"
            />
            {refreshDateLabel ? (
              <p className="text-[10px] sm:text-xs mt-0.5" style={{ color: safeTheme.text.secondary }}>
                {t(`${CR}.refreshAvailableOn`, { date: refreshDateLabel })}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 w-full sm:w-auto flex-shrink-0">
          {canCredentialReimport && hasCredentials && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              disabled={importInFlight}
              className="h-9 sm:h-10 px-3 sm:px-4 font-bold text-xs sm:text-sm"
              style={{
                background: safeTheme.surface.s2,
                border: `1px solid ${safeTheme.border.b2}`,
                borderRadius: '12px',
                color: accentGreen,
              }}
              data-testid="credential-refresh-report-btn"
            >
              {importInFlight ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <RefreshCw size={14} className="mr-2" />
              )}
              {primaryRefreshLabel}
            </Button>
          )}
          {!canRefreshNow && canCredentialReimport && !hasCredentials && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onNeedCredentials?.(provider)}
              disabled={importInFlight}
              className="h-9 sm:h-10 px-3 sm:px-4 font-bold text-xs sm:text-sm"
              style={{
                background: safeTheme.surface.s2,
                border: `1px solid ${safeTheme.border.b2}`,
                borderRadius: '12px',
                color: accentGreen,
              }}
              data-testid="connect-provider-btn"
            >
              <Key size={14} className="mr-2" />
              {t(`${CR}.connectProvider`)}
            </Button>
          )}
          {showPurchase && (
            <Button
              variant="outline"
              size="sm"
              onClick={handlePurchase}
              disabled={importInFlight}
              className="h-9 sm:h-10 px-3 sm:px-4 font-bold text-xs sm:text-sm"
              style={{
                background: safeTheme.surface.s2,
                border: `1px solid ${safeTheme.border.b2}`,
                borderRadius: '12px',
                color: safeTheme.text.secondary,
              }}
              data-testid="purchase-report-btn"
            >
              <ExternalLink size={14} className="mr-2" />
              {t(`${CR}.purchaseReport`)}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onUpload?.()}
            className="h-9 sm:h-10 px-3 sm:px-4 font-bold text-xs sm:text-sm"
            style={{
              background: safeTheme.surface.s2,
              border: `1px solid ${safeTheme.border.b2}`,
              borderRadius: '12px',
              color: accentGreen,
            }}
            data-testid="upload-html-import-btn"
          >
            <Upload size={14} className="mr-2" />
            {canCredentialReimport ? t(`${CR}.uploadReportFile`) : t(`${CR}.updateReport`)}
          </Button>
        </div>
      </div>

      {showRefreshStatus && (
        <div
          className="relative z-10 rounded-xl p-3 sm:p-3.5"
          style={{
            background: canRefreshNow
              ? (safeTheme.isDark ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.06)')
              : safeTheme.surface.s2,
            border: `1px solid ${canRefreshNow ? 'rgba(52, 211, 153, 0.18)' : safeTheme.border.b2}`,
          }}
          data-testid="credential-import-refresh-status"
        >
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {providerDisplayName && (
              <span
                className="inline-flex items-center px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-md"
                style={{
                  background: safeTheme.isDark ? 'rgba(142, 218, 182, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                  color: accentGreen,
                  border: `1px solid ${safeTheme.isDark ? 'rgba(52, 211, 153, 0.16)' : 'rgba(16, 185, 129, 0.2)'}`,
                }}
              >
                {providerDisplayName}
              </span>
            )}
            {canRefreshNow ? (
              <span
                className="inline-flex items-center px-2 py-0.5 text-[10px] sm:text-xs font-semibold rounded-md"
                style={{
                  background: safeTheme.isDark ? 'rgba(52, 211, 153, 0.16)' : 'rgba(16, 185, 129, 0.14)',
                  color: accentGreen,
                }}
                data-testid="refresh-available-pill"
              >
                {t(`${CR}.refreshAvailable`)}
              </span>
            ) : refreshDateLabel ? (
              <span
                className="text-[10px] sm:text-xs font-semibold"
                style={{ color: safeTheme.text.secondary }}
                data-testid="refresh-date-pill"
              >
                {t(`${CR}.refreshAvailable`)}
              </span>
            ) : null}
          </div>

          {canRefreshNow ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2">
                {importInFlight ? (
                  <Loader2 size={14} className="animate-spin mt-0.5 flex-shrink-0" style={{ color: accentGreen }} />
                ) : (
                  <RefreshCw size={14} className="mt-0.5 flex-shrink-0" style={{ color: accentGreen }} />
                )}
                <p className="text-[11px] sm:text-xs leading-relaxed" style={{ color: safeTheme.text.muted }}>
                  {importInFlight
                    ? t(`${CR}.startingRefreshImport`)
                    : (refreshData?.banner?.subtext || t(`${CR}.refreshAvailableBody`))}
                </p>
              </div>
              {!importInFlight && (
                <Button
                  size="sm"
                  onClick={handleManualRefresh}
                  disabled={importInFlight}
                  className="w-full sm:w-auto h-9 font-semibold text-xs sm:text-sm"
                  style={{ background: accentGreenSolid, color: '#fff', borderRadius: '12px' }}
                  data-testid="manual-refresh-report-btn"
                >
                  <RefreshCw size={14} className="mr-2" />
                  {primaryRefreshLabel}
                </Button>
              )}
            </div>
          ) : (
            <p className="text-[11px] sm:text-xs leading-relaxed" style={{ color: safeTheme.text.muted }}>
              {refreshData?.banner?.subtext || t(`${CR}.refreshNotAvailableBody`)}
            </p>
          )}

          {statusUnavailable && (
            <p className="text-[10px] mt-2 opacity-80" style={{ color: safeTheme.text.muted }}>
              {REFRESH_STATUS_UNAVAILABLE_MESSAGE}
            </p>
          )}
        </div>
      )}

      {!canCredentialReimport && (
        <p className="relative z-10 text-[10px] sm:text-xs leading-relaxed" style={{ color: safeTheme.text.muted }}>
          {t(`${CR}.uploadToUpdateHint`)}
        </p>
      )}
    </section>
  );
};

export default CredentialImportStatusCard;
